import type { ISelectItem } from '~/types/models'
import type { IShowForStatusItemDefinition } from '~/types/listPageTopButtonBarValues/models'

/**
 * Shared factories for the ~80 per-entity select-list modules in this directory.
 *
 * The Vuex-era half of this module was deleted on 2026-08-11: a module-level
 * `store` handle, `setStoreInSelectListsModule`, `loadListFactory`,
 * `loadSelectListsFactory`, `getListFromCacheFactory`, and the private
 * `getListFromCache` / `putListInCache` / `getListFromAPI`. They cached lists in
 * a `selectLists/*` Vuex module that has not existed since the Pinia migration,
 * so `store` was never assigned and every one of those paths would have thrown
 * on its first call. Nothing outside this file imported any of them.
 *
 * `getListFromAPI` also held the last bare `fetch` outside `services/http/` —
 * the one place a transport failure could still escape untyped now that the seam
 * converts them (see `fetchOrThrowNetworkError`). Deleting it closes that gap
 * rather than fixing code no caller reaches.
 *
 * What remains is pure: each factory closes over a `getList` supplied by the
 * per-entity module and performs no I/O.
 */

export function getItemByLabelFactory<T extends ISelectItem>(
  getList: () => T[]
) {
  function getItemByLabel(label: string): T {
    const list: T[] = getList()
    return list.find((item) => item.label === label)!
  }
  return getItemByLabel
}

export function getItemByIdFactory<T extends ISelectItem>(getList: () => T[]) {
  function getItemById(id: string | number): T {
    const list: T[] = getList()
    return list.find((item) => item.id === id)!
  }
  return getItemById
}

export function getItemLabelsFactory<T extends ISelectItem>(
  getList: () => T[]
) {
  /**
   * @param displayNames Menu text to show in place of a label, keyed by item
   *   id (see IShowForStatusItemDefinition.displayName).
   */
  function getItemLabels(
    displayNames: Partial<Record<T['id'], string>> = {}
  ): IShowForStatusItemDefinition[] {
    const list: T[] = getList()
    return list.map((item) => {
      const displayName = displayNames[item.id as T['id']]
      return displayName
        ? { name: item.label, displayName }
        : { name: item.label }
    })!
  }
  return getItemLabels
}
